import type { ItemPriceRow, PriceTierResponse } from '@palaccount/contracts';

/**
 * The MD-03 panel-4 tier table (owner ruling R5): ONE ROW PER TIER THE
 * TENANT HAS DEFINED — one, three or eleven, never a fixed three
 * (BR-MD-03-21). An empty box means "no special price at this tier", and it
 * is stored as an ABSENT row, never as zero (BR-MD-14-04).
 */

export interface TierPriceRow {
  tierId: string;
  tierName: string;
  /** '' = no special price (falls back to the unit price). */
  price: string;
}

/**
 * Draws the editor's rows: every ACTIVE tier in its sort order, plus any
 * inactive tier the item already carries a price for (BR-MD-03-23 — stored
 * prices survive a switch-off and must not be silently dropped on re-save).
 */
export function buildTierRows(
  tiers: readonly PriceTierResponse[],
  prices: readonly ItemPriceRow[],
): TierPriceRow[] {
  const priced = new Map(prices.map((price) => [price.tierId, price]));
  const rows: TierPriceRow[] = tiers
    .filter((tier) => tier.isActive || priced.has(tier.id))
    .map((tier) => ({
      tierId: tier.id,
      tierName: tier.name,
      price: priced.get(tier.id)?.price ?? '',
    }));
  const drawn = new Set(rows.map((row) => row.tierId));
  // A price whose tier is missing from the list (switched off AND filtered
  // out) still may not be dropped.
  for (const price of prices) {
    if (!drawn.has(price.tierId)) {
      rows.push({ tierId: price.tierId, tierName: price.tierName, price: price.price });
    }
  }
  return rows;
}

/** One edit, immutably. */
export function setTierPrice(rows: readonly TierPriceRow[], tierId: string, price: string): TierPriceRow[] {
  return rows.map((row) => (row.tierId === tierId ? { ...row, price } : row));
}

/** The write shape: emptied rows PRUNED — absence is the storage of "empty". */
export function pricesFromRows(rows: readonly TierPriceRow[]): { tierId: string; price: string }[] {
  return rows
    .filter((row) => row.price.trim() !== '')
    .map((row) => ({ tierId: row.tierId, price: row.price.trim() }));
}

/** A well-formed money box: digits, up to 2 decimals (never negative by shape). */
export function isValidTierPrice(value: string): boolean {
  return /^\d{1,13}(\.\d{1,2})?$/.test(value);
}
